import { parseDate } from 'bernie-l10n';

import { ILocalization } from '@shared-ui/localization-context';

import { DateInput, PropertyDateRangeInput } from '__generated__/api';

export class DateUtils {
  private localization: ILocalization;

  constructor(l10n: ILocalization) {
    this.localization = l10n;
    this.getISODayAfter = this.getISODayAfter.bind(this);
    this.getISODayBefore = this.getISODayBefore.bind(this);
    this.getNDaysFrom = this.getNDaysFrom.bind(this);
    this.getToday = this.getToday.bind(this);
    this.isSame = this.isSame.bind(this);
  }

  public getDaysBetween(startISODate: string | Date, endISODate: string | Date) {
    const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

    const endDateString = parseDate(endISODate).toString();
    const startDateString = parseDate(startISODate).toString();

    return Math.floor((Date.parse(endDateString) - Date.parse(startDateString)) / MILLISECONDS_PER_DAY);
  }

  public getISODayAfter(currentISODate: string | Date) {
    return this.getNDaysFrom(currentISODate, 1);
  }

  public getISODayBefore(currentISODate: string | Date) {
    return this.getNDaysFrom(currentISODate, -1);
  }

  public getNDaysFrom(currentISODate: string | Date, n: number) {
    const currentDate = parseDate(currentISODate);
    const isoDayBefore = parseDate(currentISODate);
    isoDayBefore.setDate(currentDate.getDate() + n);

    return this.localization.formatDate(isoDayBefore, { raw: 'yyyy-MM-dd' });
  }

  public getToday() {
    const todayAsDate = new Date();
    const normalizedDate = new Date(todayAsDate.getFullYear(), todayAsDate.getMonth(), todayAsDate.getDate());

    return this.localization.formatDate(normalizedDate, { raw: 'yyyy-MM-dd' });
  }

  public static isBefore(firstISODate: string | Date, secondISODate: string | Date) {
    const firstDate = parseDate(firstISODate);
    const secondDate = parseDate(secondISODate);

    return firstDate < secondDate;
  }

  public isSame(firstISODate: string | Date, secondISODate: string | Date) {
    const firstDate = parseDate(firstISODate);
    const secondDate = parseDate(secondISODate);

    return firstDate.getTime() === secondDate.getTime();
  }

  public static parseDateString(dateString: string | undefined): DateInput | undefined {
    const dateParts = dateString?.split('-');

    if (!dateParts || dateParts.length !== 3) {
      return;
    }

    return {
      day: Number.parseInt(dateParts[2], 10),
      month: Number.parseInt(dateParts[1], 10),
      year: Number.parseInt(dateParts[0], 10),
    };
  }

  public static hashDateRange(dateRange: PropertyDateRangeInput): string {
    if (!dateRange.checkInDate || !dateRange.checkOutDate) {
      return '';
    }

    return `${DateUtils.hashDateInput(dateRange.checkInDate)}--${DateUtils.hashDateInput(dateRange.checkOutDate)}`;
  }

  static hashDateInput(date: DateInput): string {
    return `${date.year}-${date.month}-${date.day}`;
  }
}
